/**
 * Раздел «Информация» — разметка 1:1 с legacy htdocs/core/templates/admin_information.tpl
 * (список: .infobox, .article/.title/.ctrl) и admin_information_edit.tpl
 * (редактор: form.add-form, .form-control.news-input, #news_text).
 *
 * Legacy: /admin/information (список) и /admin/information/{id} (0 — новый материал);
 * здесь один компонент с внутренним режимом — маршрут /admin/information задан в App.tsx.
 *
 * API: GET/POST /api/admin/content/information, GET/PUT/DELETE /api/admin/content/information/{id}
 * (Handlers\Admin\Content, таблица materials_information).
 * WYSIWYG summernote заменён на <textarea> в той же обёртке — см. notes.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { HtmlEditor } from '@/components/HtmlEditor'
import type { Material } from '@/api/types'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

export function AdminInformationPage() {
  useLegacyCss('admin', 'newapproach') // те же стили, что подключал legacy admin.tpl

  const { data, reload } = useApi<Material[]>('/api/admin/content/information')

  /** null — список; 0 — новый материал; иначе id редактируемого. */
  const [editId, setEditId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const { data: material } = useApi<Material>(
    editId !== null && editId > 0 ? `/api/admin/content/information/${editId}` : null,
  )

  useEffect(() => {
    if (material === null) return
    setTitle(material.title)
    setText(material.text)
  }, [material])

  const news = data ?? []

  const openEditor = (id: number): void => {
    setEditId(id)
    setTitle('')
    setText('')
    setOk([])
    setError([])
  }

  /** legacy news-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/information/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении'])
    }
  }

  /** legacy news-submit. */
  const submit = async (): Promise<void> => {
    if (editId === null) return
    try {
      const result =
        editId > 0
          ? await api.put<{ message: string }>(`/api/admin/content/information/${editId}`, {
              title,
              text,
            })
          : await api.post<{ id: number; message: string }>('/api/admin/content/information', {
              title,
              text,
            })
      setOk([result.message])
      setError([])
      reload()
      if (editId === 0) setEditId(null)
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении'])
      setOk([])
    }
  }

  const infobox = (
    <>
      {error.map((message, index) => (
        <p key={index} className="okke" style={{ color: '#ce4844', float: 'left' }}>
          {message}
        </p>
      ))}
      {ok.map((message, index) => (
        <p key={index} className="okke" style={{ float: 'left' }}>
          {message}
        </p>
      ))}
    </>
  )

  // ── Редактор материала (legacy admin_information_edit.tpl) ───────────────
  if (editId !== null) {
    return (
      <>
        <div className="infobox clearfix">
          {infobox}
          <div style={{ float: 'right' }}>
            <a
              className="bt"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setEditId(null)
                setOk([])
                setError([])
              }}
            >
              К списку
            </a>
          </div>
        </div>

        <form
          className="add-form"
          method="POST"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div
            className="form-control news-input"
            style={{ paddingBottom: '6px', display: 'table' }}
          >
            <span style={{ width: '120px', display: 'table-cell', color: '#999' }}>
              Название:&nbsp;
            </span>
            <input
              name="title"
              style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <br />
          <HtmlEditor value={text} onChange={setText} />
          <div style={{ textAlign: 'center' }}>
            <input
              type="submit"
              name="news-submit"
              className={`bt ${title !== '' ? 'orange' : 'green'}`}
              value={editId > 0 ? 'Сохранить изменения' : 'Добавить'}
            />
          </div>
        </form>
      </>
    )
  }

  // ── Список материалов (legacy admin_information.tpl) ─────────────────────
  return (
    <>
      <div className="infobox clearfix">
        {infobox}
        <div style={{ float: 'right' }}>
          <a
            className="bt green"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEditor(0)
            }}
          >
            добавить
          </a>
        </div>
      </div>

      <form className="form-news" method="POST" onSubmit={(e) => e.preventDefault()}>
        {news.map((item) => (
          <div key={item.id}>
            <hr />
            <div className="article">
              <div className="title">
                {item.title}
                <br />
                <span style={{ fontSize: '10pt' }}>{item.date}</span>
              </div>
              <div className="ctrl">
                <a
                  href="#"
                  className="bt orange"
                  onClick={(e) => {
                    e.preventDefault()
                    openEditor(item.id)
                  }}
                >
                  редактировать
                </a>
                <input
                  type="submit"
                  name={`news-delete[${item.id}]`}
                  className="bt red"
                  value="удалить"
                  onClick={() => void remove(item.id)}
                />
              </div>
            </div>
          </div>
        ))}
      </form>
    </>
  )
}
